import { when } from '@execonline-inc/maybe-adapter';
import clsx from 'clsx';
import { addDays, isFuture } from 'date-fns';
import { Maybe, isJust } from 'maybeasy';
import { observer } from 'mobx-react';
import * as React from 'react';
import { L } from '../../../../../../../Translations';
import { ScheduledProgramInstanceOption } from '../../../../../Types';
import { utcDateToLocalDate } from '../../../../Functions';
import { SelectingDate } from '../Types';
import { Actions } from '.';

export interface Props {
  state: SelectingDate;
  actions: Pick<Actions, 'inviting' | 'selectDate'>;
}

const handleSubmit =
  (inviting: (programIds: number[]) => void, selectedPrograms: number[]) =>
  (e: React.SyntheticEvent): void => {
    e.preventDefault();
    inviting(selectedPrograms);
  };

const checkRenewalDate =
  (daysUntilRenewalDate: number, scheduledOption: ScheduledProgramInstanceOption) =>
  (endOn: Date): Maybe<ScheduledProgramInstanceOption> =>
    when(() => endOn < addDays(new Date(), daysUntilRenewalDate), scheduledOption);

const scheduledOptionEndsBeforeRenewalDate = (
  daysUntilRenewal: number,
  scheduledOption: ScheduledProgramInstanceOption,
): Maybe<ScheduledProgramInstanceOption> =>
  scheduledOption.endOn.andThen(checkRenewalDate(daysUntilRenewal, scheduledOption));

const oneDayAfterProgramStart = (scheduledOption: ScheduledProgramInstanceOption): Date =>
  addDays(scheduledOption.startOn, 1);

const isFutureOption = (
  scheduledOption: ScheduledProgramInstanceOption,
): Maybe<ScheduledProgramInstanceOption> =>
  when(isFuture(oneDayAfterProgramStart(scheduledOption)), scheduledOption);

const selectableOption = (
  scheduledOption: ScheduledProgramInstanceOption,
  daysUntilRenewal: number,
) =>
  isFutureOption(scheduledOption).andThen((scheduledOption) =>
    scheduledOptionEndsBeforeRenewalDate(daysUntilRenewal, scheduledOption),
  );

export const selectable = (
  scheduledOption: ScheduledProgramInstanceOption,
  daysUntilRenewal: number,
): boolean => isJust(selectableOption(scheduledOption, daysUntilRenewal));

function Form({ actions, state }: Props) {
  const { programIds, shareUrl, daysUntilRenewal } = state;
  return (
    <form onSubmit={handleSubmit(actions.inviting, programIds)} tabIndex={0} className="flex">
      <ul className={clsx('items-center self-stretch px-2')}>
        {shareUrl.shareUrl.payload.programInstanceOptions.scheduled.map((instance) => (
          <li className="flex mt-4 items-center" key={instance.id}>
            <input
              className={clsx({
                'h-5 w-5 text-teal-600 mr-2 rounded-full': true,
                'cursor-pointer': selectable(instance, daysUntilRenewal),
                'opacity-50 cursor-default bg-gray-200': !selectable(instance, daysUntilRenewal),
              })}
              disabled={!selectable(instance, daysUntilRenewal)}
              type="radio"
              name="Add Program"
              checked={[...programIds].includes(instance.id)}
              onChange={() => actions.selectDate(instance.id)}
            />
            <div>
              <L
                localizeable={utcDateToLocalDate(instance.startOn)}
                format={'long-weekday-month-year'}
              />
            </div>
          </li>
        ))}
      </ul>
      <input type="submit" hidden />
    </form>
  );
}

export default observer(Form);
